/**
 * @file     CaptureStep.tsx
 * @readme   ../README.md
 * @purpose  Configurable lead capture form step with conditional fields, validation, verification, and CRM submission.
 */
'use client';

import { useCallback, useEffect, useRef, useState, type AnimationEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { RayInfoTooltip } from '@/components/ui/RayInfoTooltip';
import { useAntiSpam } from '@/services/anti-spam';
import { useFormMemory } from '@/hooks/useFormMemory';
import { useAgentMode } from '@/hooks/useAgentMode';
import { useAnalytics } from '@/hooks/useAnalytics';
import { formatPhoneInput, toE164 } from '@/utils/formatPhone';
import { normalizeEmailInput } from '@/utils/canonicalEmail';
import { PillRow, splitMulti } from './PillRow';
import type { CaptureField, FlowConfig, FlowStep } from '../types';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { trackFieldBlurred, trackFieldFocused, trackSubmit } from '@/features/deal-finder/funnelTracker';
import { trackLeadConversion, trackMetaLeadConversion } from '@/services/analytics/leadConversion';
import { willBrowserEmitGa4 } from '@/services/analytics/gtagDelivery';
import { useReltaTrack } from '@/hooks/useReltaTrack';
import { createSupabaseBrowserClient } from '@/services/supabase/client';
import { scrollStepIntoView } from '@/utils/scrollStepIntoView';
import { AntiSpamHoneypot } from '@/components/forms/AntiSpamHoneypot';
import { PhoneVerifyPending } from '@/components/lead-capture/PhoneVerifyPending';
import { createLeadFlowSubmissionId } from '../submissionId';
import {
  getProfileAge,
  queuePreferenceSyncMany,
  readProfile,
  writeDataPoints,
} from '@/services/visitor-profile';
import { withAppointedLink } from '@/components/lead-capture/withAppointedLink';
import { ConsentChoices, useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { consentChoicesPayload } from '@/components/lead-capture/consentPayload';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import {
  fileVowPartialLead,
  fileVowSignupLead,
  parkVowPhone,
} from '../vowOptIn';
import { FlowSurface } from '../components/FlowSurface';
import { FlowHeading } from '../components/FlowHeading';
import { flowColors, flowPrimaryButtonSx, flowRadius } from '../components/flowKitTokens';

type FileRef = { path: string; fileName: string; contentType: string; size: number };
type SignedUpload = { path: string; token: string; fileName: string; contentType: string; size: number };

async function uploadFilesToSupabase(files: File[]): Promise<FileRef[]> {
  const payload = {
    files: files.map((f) => ({
      fileName: f.name,
      contentType: f.type || 'application/octet-stream',
      size: f.size,
    })),
  };
  const res = await secureFetch('/api/leads/upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Could not get upload URLs');
  const json = (await res.json()) as { success: boolean; bucket: string; uploads: SignedUpload[] };
  if (!json.success || !Array.isArray(json.uploads) || json.uploads.length !== files.length) {
    throw new Error('Upload URL response invalid');
  }

  const supabase = createSupabaseBrowserClient();
  const refs: FileRef[] = [];
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const upload = json.uploads[i];
    const { error } = await supabase.storage
      .from(json.bucket)
      .uploadToSignedUrl(upload.path, upload.token, file, { contentType: upload.contentType });
    if (error) throw error;
    refs.push({
      path: upload.path,
      fileName: upload.fileName,
      contentType: upload.contentType,
      size: upload.size,
    });
  }
  return refs;
}

type CaptureStepProps = {
  step: FlowStep;
  flowConfig: FlowConfig;
  answers: Record<string, string>;
  onAnswer?: (key: string, value: string) => void;
  files?: File[];
  sourcePage: string;
  submissionId?: string;
  onSubmitSuccess: (verifiedEmail?: string) => void;
  onFieldInteraction?: (fieldName: string) => void;
};

export const CaptureStep = ({ step, flowConfig, answers, onAnswer, files, sourcePage, submissionId, onSubmitSuccess, onFieldInteraction }: CaptureStepProps) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const { setHoneypotValue, getAntiSpamMetadata } = useAntiSpam();
  const rootRef = useRef<HTMLDivElement>(null);
  const fallbackSubmissionIdRef = useRef<string | null>(null);

  // Browsers scroll a just-autofilled input into view, which on mobile yanks the
  // form down/out of the viewport. Re-anchor the capture card afterwards; the
  // helper is guarded and no-ops when the form is already in view.
  const handleAutofill = useCallback((e: AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'reltaCaptureAutofill') {
      scrollStepIntoView(rootRef.current, 60);
    }
  }, []);
  const formMemory = useFormMemory();
  const agentMode = useAgentMode();
  const { trackEvent } = useAnalytics();
  const reltaTrack = useReltaTrack();

  const [values, setValues] = useState(() => {
    const mem = formMemory.getMemory();
    return {
      name: mem.name ?? '',
      firstName: mem.firstName ?? '',
      lastName: mem.lastName ?? '',
      email: mem.email ?? '',
      phone: mem.phone ?? '',
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreementChecks, setAgreementChecks] = useState<Record<string, boolean>>({});
  const consent = useConsentChoices();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState(false);
  const [verifyPending, setVerifyPending] = useState<{
    email: string;
    channel?: 'sms';
    phone?: string;
    submissionId?: string;
  } | null>(null);

  const accountCapture = agentMode.active ? undefined : step.accountCapture;
  const landing = flowConfig.landing;
  const startedAt = useRef(Date.now());
  // Latched once the account submit is accepted or the partial is filed, so a
  // later unmount or page hide never files a second lead.
  const vowLeadFiled = useRef(false);
  // Every capture takes first name, last name, email and mobile, all required
  // (Ray, 2026-10): the lead becomes an account whose listing access ends at
  // an SMS verification wall.
  const captureFields = step.captureFields ?? ['firstName', 'lastName', 'email', 'phone'];
  const requiredFields = new Set<CaptureField>(step.requiredCaptureFields ?? captureFields);
  const showsField = (field: CaptureField): boolean => captureFields.includes(field);
  // A flow with its own `sms` agreement box uses that tick; every other flow
  // that takes a phone shows the shared optional checkbox.
  const hasSmsAgreement = (step.agreements ?? []).some((agreement) => agreement.id === 'sms');
  const showSmsCheckbox = showsField('phone') && !hasSmsAgreement && !step.hideSmsOptIn && !agentMode.active;
  const smsChecked = hasSmsAgreement ? Boolean(agreementChecks.sms) : showSmsCheckbox && consent.values.sms;
  const showEmailConsent = showsField('email') && !agentMode.active;
  const emailChecked = showEmailConsent && consent.values.email;
  const requiresPhone = requiredFields.has('phone');
  const emailValue = normalizeEmailInput(values.email);
  const submittedName = showsField('name')
    ? values.name.trim()
    : [values.firstName.trim(), values.lastName.trim()].filter(Boolean).join(' ');

  // Fold only the unit/suite into the street line. City and postal travel as
  // their own fields so the CRM can populate its city / postal columns.
  // Concatenating them here left those empty and duplicated the city inside
  // the street. answers.unit is also persisted via `answers`.
  // The unit is only prefixed when the user did not already type "Unit",
  // otherwise the line reads "Unit Unit 205".
  const unit = answers.unit?.trim();
  const unitLabel = unit ? (/^(unit|suite|apt)\b/i.test(unit) ? unit : `Unit ${unit}`) : null;
  const fullAddress = answers.address
    ? [unitLabel, answers.address].filter(Boolean).join(', ')
    : undefined;

  const setField = (field: CaptureField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    formMemory.saveField(field, value);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (requiredFields.has('name') && !values.name.trim()) {
      newErrors.name = t('dealFinder.capture.errors.nameRequired', { defaultValue: 'Name is required' });
    }
    if (requiredFields.has('firstName') && !values.firstName.trim()) {
      newErrors.firstName = t('homeValueFlow.capture.firstNameRequired', { defaultValue: 'First name is required' });
    }
    if (requiredFields.has('lastName') && !values.lastName.trim()) {
      newErrors.lastName = t('homeValueFlow.capture.lastNameRequired', { defaultValue: 'Last name is required' });
    }
    if (requiredFields.has('email') && !emailValue) {
      newErrors.email = t('leadFlow.capture.errors.emailRequired', { defaultValue: 'Enter your email address.' });
    } else if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      newErrors.email = t('leadFlow.capture.errors.emailInvalid', { defaultValue: 'Enter an email address, like name@example.com.' });
    }
    if (requiresPhone && !toE164(values.phone)) {
      newErrors.phone = t('forms.contact.phoneInvalid', { defaultValue: 'Enter a 10-digit mobile number.' });
    }
    for (const g of step.pillGroups ?? []) {
      if (g.required && splitMulti(answers[g.answerKey] ?? '').length === 0) {
        newErrors[g.answerKey] = t('dealFinder.capture.errors.selectRequired', { defaultValue: 'Please make a selection' });
      }
    }
    for (const agreement of step.agreements ?? []) {
      if (agreement.required && !agreementChecks[agreement.id]) {
        newErrors[`agreement:${agreement.id}`] = t('dealFinder.capture.errors.agreementRequired', {
          defaultValue: 'Please tick this box to continue',
        });
      }
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = useCallback(async () => {
    trackSubmit('attempted');
    trackEvent({
      eventName: 'lead_form_submit_attempted',
      eventType: 'conversion',
      category: 'lead_flow',
      label: flowConfig.flowId,
      pagePath: sourcePage,
      metadata: { flow_id: flowConfig.flowId, flow_type: flowConfig.leadType, source_page: sourcePage },
    });
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      trackEvent({
        eventName: 'lead_form_validation_error',
        eventType: 'form',
        category: 'lead_flow',
        label: flowConfig.flowId,
        pagePath: sourcePage,
        metadata: { flow_id: flowConfig.flowId, flow_type: flowConfig.leadType, source_page: sourcePage },
      });
      /* fire-and-forget, intentional: best-effort audit trail so a blocked
       * submission is still visible in /admin/recovery instead of vanishing.
       * The answers ride along because everything the person already told us
       * lives in client state only, and without it a recovered seller lead has
       * no address for the CRM to accept. */
      secureFetch('/api/leads/log-validation-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flowType: flowConfig.leadType,
          sourcePage,
          submissionId: submissionId ?? fallbackSubmissionIdRef.current,
          email: emailValue || undefined,
          phone: values.phone.trim() || undefined,
          name: submittedName || undefined,
          fields: Object.keys(validationErrors),
          payload: {
            flowId: flowConfig.flowId,
            address: fullAddress,
            city: answers.city?.trim() || undefined,
            postalCode: answers.postal_code?.trim() || undefined,
            timeline: answers.timeline || undefined,
            propertyType: answers.propertyType || undefined,
            sellerGoal: answers.goal || undefined,
            propertyCondition: answers.condition || undefined,
            ownsProperty: answers.ownsProperty || undefined,
            notes: answers.notes || answers.evidence || undefined,
            answers,
          },
        }),
      }).catch(() => {});
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const tags = flowConfig.buildTags?.(answers) ?? [];
      const qualification = flowConfig.buildQualification?.(answers) ?? 'warm';
      const stableSubmissionId =
        submissionId ??
        (fallbackSubmissionIdRef.current ??= createLeadFlowSubmissionId());

      let fileRefs: Array<{ path: string; fileName: string; contentType: string; size: number }> = [];
      if (files && files.length > 0) {
        fileRefs = await uploadFilesToSupabase(files);
      }

      if (answers.ownsProperty) {
        const points = [{
          key: 'ownsProperty',
          value: answers.ownsProperty,
          source: sourcePage,
        }];
        try {
          writeDataPoints(points);
          queuePreferenceSyncMany({ ownsProperty: answers.ownsProperty });
        } catch {
          /* fire-and-forget, intentional */
        }
      }

      const profile = readProfile();
      const visitorContext = profile
        ? {
            profileAgeDays: getProfileAge() ?? 0,
            pagesVisited: (profile.dataPoints['pagesVisited']?.value as string[]) ?? [],
            dataPoints: Object.fromEntries(
              Object.entries(profile.dataPoints).map(([key, entry]) => [
                key,
                { value: entry.value, source: entry.source },
              ]),
            ),
          }
        : undefined;

      const payload = {
        type: flowConfig.leadType,
        submissionId: stableSubmissionId,
        name: submittedName,
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
        email: emailValue,
        phone: values.phone.trim() ? (toE164(values.phone) ?? values.phone.trim()) : undefined,
        address: fullAddress,
        // `city` comes from the address autocomplete; a city-only step (the
        // pre-approval `location` question) has no autocomplete and stores the
        // typed city under its own step id instead.
        city: answers.city?.trim() || answers.location?.trim() || undefined,
        postalCode: answers.postal_code?.trim() || undefined,
        timeline: answers.timeline || undefined,
        propertyType: answers.propertyType || undefined,
        // A pill group with answerKey `bedsMin` (the buyer-match bedrooms
        // question) lands in the leads.beds_min column downstream.
        bedsMin: answers.bedsMin || undefined,
        sellerGoal: answers.goal || undefined,
        propertyCondition: answers.condition || undefined,
        ownsProperty: answers.ownsProperty || undefined,
        notes: answers.notes || answers.evidence || undefined,
        sourcePage,
        flowId: flowConfig.flowId,
        answers: answers,
        tags,
        qualification,
        ...(agentMode.active
          ? {}
          : consentChoicesPayload({ email: emailChecked, sms: smsChecked }, values.phone.trim(), { locale: i18n?.language })),
        fileRefs,
        antiSpam: getAntiSpamMetadata(),
        ...(visitorContext ? { visitorContext } : {}),
        ...(agentMode.active ? { agentSubmission: true } : {}),
        ...(landing
          ? {
              landingSlug: landing.slug,
              landingVariant: landing.variant,
              landingIntent: landing.intent,
              landingLocale: landing.pageLocale,
            }
          : {}),
      };

      // Agent mode: submit directly without verification gate so the admin's
      // session is never replaced by the prospect's magic link.
      if (agentMode.active || step.submitDirect) {
        const { submitLead } = await import('@/services/leads/submitLead');
        const response = await submitLead(
          { ...payload, fileRefs },
          {
            sourcePage,
            flowId: flowConfig.flowId,
            landingSlug: landing?.slug,
            landingVariant: landing?.variant,
            landingIntent: landing?.intent,
            landingLocale: landing?.pageLocale,
          },
        );
        if (response.ok) {
          trackSubmit('succeeded');
          onSubmitSuccess();
        } else {
          setSubmitMessage(t('dealFinder.capture.errors.submitFailed', { defaultValue: 'Something went wrong. Please try again.' }));
          setSubmitError(true);
        }
        return;
      }

      // Which leg reports the conversion to GA4. GA4 does not deduplicate
      // `generate_lead`, so the browser event below and the route handler's
      // Measurement Protocol leg have to be mutually exclusive.
      const browserEmitsGa4 = await willBrowserEmitGa4();
      const res = await secureFetch('/api/leads/pending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          flowType: flowConfig.pendingFlowType ?? flowConfig.leadType,
          ga4ConversionLeg: browserEmitsGa4 ? 'client' : 'server',
          // Account capture keeps the email confirmation as the gate unless
          // the step verifies before it advances, where the texted code leads.
          // The number captured here prefills the VOW opt-in either way.
          ...(accountCapture && !step.verifyBeforeAdvance ? { verifyChannel: 'email' } : {}),
        }),
      });
      const json = (await res.json()) as {
        ok?: boolean;
        pending?: string;
        ref?: string;
        error?: string;
        channel?: string;
        phone?: string;
        submissionId?: string;
        phoneVerified?: boolean;
      };

      if (json.ok && json.pending === 'verify') {
        trackSubmit('succeeded');
        reltaTrack('lead.submit', {
          lead_type: flowConfig.leadType,
          has_email: Boolean(emailValue),
          has_phone: Boolean(values.phone.trim()),
        });
        // No separate "submitted" GA4 event here: it duplicated
        // generate_lead_<category> below for every completed submission, and
        // fired even on spam-quarantined ones (no `json.ref`). The capture
        // step's `lead_flow_step_completed` event (fired by FlowRenderer's
        // advance()) already marks this step done for funnel reporting.
        // Conversion counts at submit, not at email verification ("softer"
        // optimization signal for Ads). `ref` is absent on spam-rejected
        // submissions, so bots never fire it. GA4 stays on one leg. Meta has
        // no server leg, so the browser reports Lead either way.
        if (json.ref) {
          const conversion = {
            flowId: flowConfig.flowId,
            sourcePage,
            referenceId: `pending-lead-${json.ref}`,
            landingSlug: landing?.slug,
            landingVariant: landing?.variant,
            landingIntent: landing?.intent,
            landingLocale: landing?.pageLocale,
          };
          if (browserEmitsGa4) {
            trackLeadConversion(flowConfig.leadType, conversion);
          } else {
            trackMetaLeadConversion(flowConfig.leadType, conversion);
          }
        }
        if (accountCapture) {
          vowLeadFiled.current = true;
          parkVowPhone(values.phone);
          void fileVowSignupLead({
            name: submittedName,
            firstName: values.firstName.trim() || undefined,
            lastName: values.lastName.trim() || undefined,
            email: emailValue,
            phone: toE164(values.phone) ?? values.phone.trim(),
            sourcePage,
            elapsedSeconds: (Date.now() - startedAt.current) / 1000,
          }).catch(() => {
            // Non-blocking, like VowAccessModal: the seller lead already landed.
          });
        }
        if (json.phoneVerified) {
          onSubmitSuccess(emailValue);
        } else if (json.channel === 'sms' && json.phone) {
          // Verification happens right here by text; hold the step so the
          // code entry is visible even for flows that normally advance.
          setVerifyPending({
            email: emailValue,
            channel: 'sms',
            phone: json.phone,
            submissionId: json.submissionId ?? submissionId,
          });
        } else if (step.advanceOnPendingVerification) {
          onSubmitSuccess(emailValue);
        } else {
          setVerifyPending({ email: emailValue, submissionId: stableSubmissionId });
        }
        setSubmitError(false);
      } else {
        trackSubmit('failed');
        trackEvent({
          eventName: 'lead_form_submit_failed',
          eventType: 'conversion',
          category: 'lead_flow',
          label: flowConfig.flowId,
          pagePath: sourcePage,
          metadata: { flow_id: flowConfig.flowId, flow_type: flowConfig.leadType, source_page: sourcePage },
        });
        setSubmitMessage(t('dealFinder.capture.errors.submitFailed', { defaultValue: 'Something went wrong. Please try again.' }));
        setSubmitError(true);
      }
    } catch {
      trackSubmit('failed');
      trackEvent({
        eventName: 'lead_form_submit_failed',
        eventType: 'conversion',
        category: 'lead_flow',
        label: flowConfig.flowId,
        pagePath: sourcePage,
        metadata: { flow_id: flowConfig.flowId, flow_type: flowConfig.leadType, source_page: sourcePage },
      });
      setSubmitMessage(t('dealFinder.capture.errors.submitFailed', { defaultValue: 'Something went wrong. Please try again.' }));
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, answers, flowConfig, sourcePage, submissionId, onSubmitSuccess, getAntiSpamMetadata, t, trackEvent, reltaTrack, step.advanceOnPendingVerification, step.verifyBeforeAdvance, step.submitDirect, submittedName, accountCapture, landing, smsChecked, emailChecked, i18n?.language]);

  // Leaving the account step with an email typed files the VOW partial, the
  // flow's equivalent of closing VowAccessModal mid-registration.
  const partialRef = useRef({ email: emailValue, name: submittedName, phone: values.phone });
  partialRef.current = { email: emailValue, name: submittedName, phone: values.phone };
  useEffect(() => {
    if (!accountCapture) return undefined;
    const filePartial = () => {
      const { email, name, phone } = partialRef.current;
      if (vowLeadFiled.current || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      vowLeadFiled.current = true;
      void fileVowPartialLead({
        name,
        email,
        phone: phone.trim(),
        sourcePage,
        elapsedSeconds: (Date.now() - startedAt.current) / 1000,
      }).catch(() => {
        // Recovery is best-effort and must never hold the page.
      });
    };
    window.addEventListener('pagehide', filePartial);
    return () => {
      window.removeEventListener('pagehide', filePartial);
      filePartial();
    };
  }, [accountCapture, sourcePage]);

  // The email fallback of a verify-first step: once the link is opened, in
  // this tab or another, carry on to the result.
  const awaitingEmailRef = verifyPending && verifyPending.channel !== 'sms' && step.verifyBeforeAdvance
    ? verifyPending.submissionId
    : undefined;
  const awaitingEmail = verifyPending?.email;
  useEffect(() => {
    if (!awaitingEmailRef) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/leads/pending/status?submissionId=${encodeURIComponent(awaitingEmailRef)}`, {
          cache: 'no-store',
        });
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as { verified?: boolean };
        if (json.verified && !cancelled) {
          cancelled = true;
          trackEvent({
            eventName: 'lead_email_confirmed',
            eventType: 'funnel',
            category: 'lead_flow',
            label: flowConfig.flowId,
            metadata: { flow_type: flowConfig.leadType, step_id: step.id },
          });
          onSubmitSuccess(awaitingEmail);
        }
      } catch (error: unknown) {
        // The next tick retries; the link itself still verifies the lead.
        void error;
      }
    };
    const timer = setInterval(() => void poll(), 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [awaitingEmailRef, awaitingEmail, flowConfig.flowId, flowConfig.leadType, onSubmitSuccess, step.id, trackEvent]);

  if (verifyPending?.channel === 'sms' && verifyPending.phone && verifyPending.submissionId) {
    return (
      <FlowSurface>
        <PhoneVerifyPending
          submissionId={verifyPending.submissionId}
          phone={verifyPending.phone}
          email={verifyPending.email}
          onVerified={() => onSubmitSuccess(verifyPending.email)}
          onUseEmailInstead={() => setVerifyPending({ email: verifyPending.email, submissionId: verifyPending.submissionId })}
        />
      </FlowSurface>
    );
  }

  if (verifyPending) {
    return (
      <FlowSurface role="status">
        <FlowHeading
          subtext={step.verifyPendingBodyKey
            ? t(step.verifyPendingBodyKey, { defaultValue: step.verifyPendingBodyDefault, email: verifyPending.email })
            : t('capture.verifyPending.body', {
                defaultValue: 'Open the link sent to {{email}} to confirm.',
                email: verifyPending.email,
              })}
        >
          {step.verifyPendingHeadlineKey
            ? t(step.verifyPendingHeadlineKey, { defaultValue: step.verifyPendingHeadlineDefault })
            : t('capture.verifyPending.headline', { defaultValue: 'Confirm your email' })}
        </FlowHeading>
        <Typography sx={{ color: flowColors.textMuted, fontSize: 13.5, lineHeight: 1.5 }}>
          {t('capture.verifyPending.spamHint', { defaultValue: 'Not there? Check spam.' })}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            setVerifyPending(null);
            setValues((v) => ({ ...v, email: '' }));
            formMemory.saveField('email', '');
          }}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', minHeight: 44, px: 0, color: flowColors.textSecondary, textDecoration: 'underline', textUnderlineOffset: '3px' }}
        >
          {t('capture.verifyPending.useDifferentEmail', { defaultValue: 'Use a different email' })}
        </Button>
      </FlowSurface>
    );
  }

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: `${flowRadius.md}px` } };
  const ctaLabel = step.ctaKey
    ? t(step.ctaKey, { defaultValue: step.ctaDefault })
    : t('common.submit', { defaultValue: 'Submit' });
  const headline = t(step.headlineKey, { defaultValue: step.headlineDefault });

  return (
    <FlowSurface
      ref={rootRef}
      onAnimationStart={handleAutofill}
      sx={{
        // Fires a no-op animation the instant Chrome/Safari autofills a field,
        // letting onAnimationStart re-anchor the form (see handleAutofill).
        '@keyframes reltaCaptureAutofill': { from: {}, to: {} },
        '& input:-webkit-autofill': { animationName: 'reltaCaptureAutofill' },
      }}
    >
        {step.motivator ? (() => {
          const count = step.motivator.compute(answers);
          if (count == null) return null;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 2,
                borderRadius: `${flowRadius.lg}px`,
                backgroundColor: flowColors.surfaceSunken,
              }}
            >
              <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1, color: flowColors.link, fontVariantNumeric: 'tabular-nums' }}>
                {count}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: '20px', color: theme.colors.textSecondary }}>
                {t(step.motivator.textKey, { defaultValue: step.motivator.textDefault })}
              </Typography>
            </Box>
          );
        })() : null}
        <FlowHeading
          subtext={step.subtextKey ? withAppointedLink(t(step.subtextKey, { defaultValue: step.subtextDefault })) : undefined}
        >
          {headline}
          {/^I[ '"]/.test(headline) && <RayInfoTooltip />}
        </FlowHeading>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${theme.layout.form.fieldGap}px` }}>
          {showsField('name') ? (
            <TextField
              fullWidth
              name="name"
              autoComplete="name"
              required={requiredFields.has('name')}
              label={t('homeValueFlow.capture.nameLabel', { defaultValue: 'Name' })}
              placeholder={t('homeValueFlow.capture.namePlaceholder', { defaultValue: 'Your full name' })}
              value={values.name}
              error={Boolean(errors.name)}
              helperText={errors.name}
              onChange={(e) => setField('name', e.target.value)}
              onFocus={() => {
                onFieldInteraction?.('name');
                trackFieldFocused('lead_capture', 'name');
              }}
              onBlur={() => trackFieldBlurred('lead_capture', 'name', values.name.trim().length)}
              sx={inputSx}
            />
          ) : null}
          {showsField('firstName') || showsField('lastName') ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: showsField('firstName') && showsField('lastName') ? '1fr 1fr' : '1fr',
                },
                gap: `${theme.layout.form.fieldGap}px`,
              }}
            >
              {showsField('firstName') ? (
                <TextField
                  fullWidth
                  name="given-name"
                  autoComplete="given-name"
                  required={requiredFields.has('firstName')}
                  label={t('homeValueFlow.capture.firstNameLabel', { defaultValue: 'First name' })}
                  placeholder={t('homeValueFlow.capture.firstNamePlaceholder', { defaultValue: 'Your first name' })}
                  value={values.firstName}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                  onFocus={() => {
                    onFieldInteraction?.('firstName');
                    trackFieldFocused('lead_capture', 'firstName');
                  }}
                  onBlur={() => trackFieldBlurred('lead_capture', 'firstName', values.firstName.trim().length)}
                  sx={inputSx}
                />
              ) : null}
              {showsField('lastName') ? (
                <TextField
                  fullWidth
                  name="family-name"
                  autoComplete="family-name"
                  required={requiredFields.has('lastName')}
                  label={t('homeValueFlow.capture.lastNameLabel', { defaultValue: 'Last name' })}
                  placeholder={t('homeValueFlow.capture.lastNamePlaceholder', { defaultValue: 'Your last name' })}
                  value={values.lastName}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                  onFocus={() => {
                    onFieldInteraction?.('lastName');
                    trackFieldFocused('lead_capture', 'lastName');
                  }}
                  onBlur={() => trackFieldBlurred('lead_capture', 'lastName', values.lastName.trim().length)}
                  sx={inputSx}
                />
              ) : null}
            </Box>
          ) : null}
          {showsField('email') ? (
            <TextField
              fullWidth
              type="email"
              name="email"
              autoComplete="email"
              required={requiredFields.has('email')}
              inputMode="email"
              label={t('homeValueFlow.capture.emailLabel', { defaultValue: 'Email' })}
              placeholder={t('homeValueFlow.capture.emailPlaceholder', { defaultValue: 'name@email.com' })}
              value={values.email}
              error={Boolean(errors.email)}
              helperText={errors.email}
              onChange={(e) => setField('email', e.target.value.replace(/\s+/g, ''))}
              onFocus={() => {
                onFieldInteraction?.('email');
                trackFieldFocused('lead_capture', 'email');
              }}
              onBlur={() => trackFieldBlurred('lead_capture', 'email', emailValue.length)}
              sx={inputSx}
            />
          ) : null}
          {showsField('phone') ? (
            <TextField
              fullWidth
              type="tel"
              name="phone"
              autoComplete="tel"
              required={requiresPhone}
              inputMode="tel"
              label={
                requiresPhone
                  ? t('forms.contact.phoneLabel', { defaultValue: 'Mobile phone' })
                  : t('homeValueFlow.capture.phoneOptionalLabel', { defaultValue: 'Phone (optional)' })
              }
              placeholder={t('homeValueFlow.capture.phonePlaceholder', { defaultValue: '(604) 555-0123' })}
              value={values.phone}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              onChange={(e) => setField('phone', formatPhoneInput(e.target.value))}
              onFocus={() => {
                onFieldInteraction?.('phone');
                trackFieldFocused('lead_capture', 'phone');
              }}
              onBlur={() => trackFieldBlurred('lead_capture', 'phone', values.phone.trim().length)}
              sx={inputSx}
            />
          ) : null}
          <AntiSpamHoneypot onValueChange={setHoneypotValue} />
        </Box>

        {(step.pillGroups ?? []).map((group) => (
          <Box key={group.answerKey}>
            <PillRow
              group={group}
              value={answers[group.answerKey] ?? ''}
              onChange={(value) => {
                onAnswer?.(group.answerKey, value);
                setErrors((prev) => ({ ...prev, [group.answerKey]: '' }));
              }}
              columns={group.multi ? 3 : 4}
            />
            {errors[group.answerKey] ? (
              <Typography role="alert" sx={{ fontSize: 13, color: flowColors.statusError, mt: 1 }}>
                {errors[group.answerKey]}
              </Typography>
            ) : null}
          </Box>
        ))}

        {(step.agreements ?? []).length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {(step.agreements ?? []).map((agreement) => (
              <Box key={agreement.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(agreementChecks[agreement.id])}
                      onChange={(e) => {
                        setAgreementChecks((prev) => ({ ...prev, [agreement.id]: e.target.checked }));
                        setErrors((prev) => ({ ...prev, [`agreement:${agreement.id}`]: '' }));
                      }}
                      size="small"
                      sx={{ pt: 0.25, alignSelf: 'flex-start' }}
                    />
                  }
                  sx={{ alignItems: 'flex-start', mr: 0 }}
                  label={
                    <Typography sx={{ fontSize: 13, lineHeight: '19px', color: theme.colors.textSecondary }}>
                      {t(agreement.labelKey, { defaultValue: agreement.labelDefault })}
                      {agreement.id === 'contact' ? (
                        <>
                          {' '}
                          <Link href="/privacy" style={{ color: 'inherit' }}>
                            {t('forms.smsCompliance.privacyPolicy', { defaultValue: 'Privacy Policy' })}
                          </Link>
                          {' · '}
                          <Link href="/terms" style={{ color: 'inherit' }}>
                            {t('forms.smsCompliance.termsOfService', { defaultValue: 'Terms of Service' })}
                          </Link>
                        </>
                      ) : null}
                    </Typography>
                  }
                />
                {errors[`agreement:${agreement.id}`] ? (
                  <Typography role="alert" sx={{ fontSize: 13, color: flowColors.statusError, ml: 4 }}>
                    {errors[`agreement:${agreement.id}`]}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : null}

        {step.disclosureKey ? (
          // Regulated disclosure: always visible, directly above the submit
          // button. Never collapse, truncate, or move it below the CTA.
          <Box
            sx={{
              borderRadius: `${flowRadius.lg}px`,
              backgroundColor: flowColors.surfaceSunken,
              px: 1.75,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: 12.5, lineHeight: '19px', color: theme.colors.textSecondary }}>
              {t(step.disclosureKey, { defaultValue: step.disclosureDefault })}
              {step.disclosureLinkHref ? (
                <>
                  {' '}
                  <Link href={step.disclosureLinkHref} style={{ color: 'inherit', fontWeight: 500 }}>
                    {t(step.disclosureLinkLabelKey ?? '', {
                      defaultValue: step.disclosureLinkLabelDefault ?? 'Full terms',
                    })}
                  </Link>
                </>
              ) : null}
            </Typography>
          </Box>
        ) : null}

        <ConsentChoices
          phone={showSmsCheckbox}
          email={showEmailConsent}
          values={consent.values}
          onChange={consent.setValues}
          disabled={isSubmitting}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          onClick={handleSubmit}
          sx={flowPrimaryButtonSx}
        >
          {isSubmitting
            ? t('common.submitting', { defaultValue: 'Submitting...' })
            : ctaLabel}
        </Button>

        {(step.agreements ?? []).length === 0 ? (
          // Terms and Privacy, for flows without explicit agreement checkboxes.
          // A step with agreements already said this above the button, with a tick.
          <FormConsentLine />
        ) : null}

        {step.privacyNoticeKey ? (
          // A flow-specific notice the one consent line cannot carry, such as
          // the pre-approval file going to an independent broker.
          <Typography variant="caption" sx={{ display: 'block', color: theme.colors.textSecondary, lineHeight: 1.5 }}>
            {withAppointedLink(t(step.privacyNoticeKey, { defaultValue: step.privacyNoticeDefault }))}
          </Typography>
        ) : null}

        {submitMessage ? (
          <Typography
            role={submitError ? 'alert' : 'status'}
            sx={{
              fontSize: 14,
              color: submitError ? flowColors.statusError : flowColors.statusSuccess,
              textAlign: 'center',
            }}
          >
            {submitMessage}
          </Typography>
        ) : null}
    </FlowSurface>
  );
};
